import { SessionCustomerQuery } from "graphql/queries/generated/SessionCustomer"
import { SessionLanguagesQuery } from "graphql/queries/generated/SessionLanguages"
import { SessionPermissionsQuery } from "graphql/queries/generated/SessionPermissions"
import { SessionScopesQuery } from "graphql/queries/generated/SessionScopes"
import { SessionSettingsQuery } from "graphql/queries/generated/SessionSettings"
import { SessionUserQuery } from "graphql/queries/generated/SessionUser"
import { SessionUsersQuery } from "graphql/queries/generated/SessionUsers"
import { TutorialProgression } from "features/Tutorial"
import { create } from "zustand"

export type CustomerSession = SessionCustomerQuery["me"]["customer"]
type PermissionsSession = SessionPermissionsQuery["me"]["permissions"]
type ScopesSession = SessionScopesQuery["me"]["scopes"]
type SettingsSession = SessionSettingsQuery["me"]["settings"]
type UsersSession = SessionUsersQuery["me"]["users"]
type LanguagesSession = SessionLanguagesQuery["languages"]
type UserSession = SessionUserQuery["me"]

export interface SessionState {
  customer?: CustomerSession,
  languages: LanguagesSession,
  permissions: PermissionsSession,
  scopes: ScopesSession,
  settings?: SettingsSession,
  users?: UsersSession,
  user?: UserSession,
  tutorialProgression?: TutorialProgression,
  authentication: {
    isAuthenticated: boolean,
    hasFailed: boolean,
    lastFailedAttempt?: string | undefined,
    reason?: string,
  },
}

export interface SessionActions {
  setCustomer: (customer: CustomerSession) => void,
  setPermissions: (permissions: PermissionsSession) => void,
  setScopes: (scopes: ScopesSession) => void,
  setSettings: (settings: SettingsSession) => void,
  setLanguages: (languages: LanguagesSession) => void,
  setUsers: (users: UsersSession) => void,
  setUser: (user: UserSession) => void,
  setTutorialProgression: (tutorialProgression: TutorialProgression) => void,

  setAuthentication: (auth: SessionState["authentication"]) => void,
  resetSession: () => void,
}

const defaultSession: SessionState = {
  authentication: {
    hasFailed: false,
    isAuthenticated: false,
    lastFailedAttempt: undefined,
    reason: undefined,
  },
  customer: {
    canSendSMS: false,
    clusterSize: 1,
    code_name: "",
    customerContacts: [],
    id: 1,
    isClusterViolated: false,
    language: {
      code: "",
      localizedLabel: "",
      translatedLabel: "",
    },
    leds: [],
    name: "",
    packages: [],
    recipientsCount: 0,
    timezone: "",
  },
  languages: [],
  permissions: [],
  scopes: [],
  settings: undefined,
  tutorialProgression: undefined,
  user: undefined,
  users: undefined,
}

export const useSessionStore = create<SessionState & SessionActions>()((set) => ({
  ...defaultSession,

  resetSession: () => set(() => ({ ...defaultSession })),
  setAuthentication: (authentication) => set(() => ({ authentication })),
  setCustomer: (customer) => set(() => ({ customer })),
  setLanguages: (languages) => set(() => ({ languages })),
  setPermissions: (permissions) => set(() => ({ permissions })),
  setScopes: (scopes) => set(() => ({ scopes })),

  setSettings: (settings) => set(() => ({ settings })),
  setTutorialProgression: (tutorialProgression) => set(() => ({ tutorialProgression })),
  setUser: (user) => set(() => ({ user })),
  setUsers: (users) => set(() => ({ users })),
}))
